import React, { useState, useEffect } from "react";
import { Row, Col } from "reactstrap";
import useCustomFecthQuery from "../../../../Hooks/useCustomFecthQuery";
import FormSMS from "./FormSMS";
import OffCanvas from "../../../OffCanvas/OffCanvas";
import TableSms from "./TableSms";
import { BsQuestionCircleFill } from "react-icons/bs";
import { BsChatLeftText } from "react-icons/bs";
import ButtonIcon from "../../../Buttons/ButtonIcon";
import Tooltip from "../../../../utils/toolTip";
import AIChat from "./AIChat";

const Sms = ({ eventDetails, token, userID, isLoading }) => {
  const [isSMSOpen, setIsSMSOpen] = useState(false);
  const [isChatAiOpen, setIsChatAiOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const canvasWidth = isMobile ? "100%" : 400;

  const key = ["allSMS", eventDetails?._id];
  const {
    data: getAllSMS,
    error: errorGetAllSMS,
    isLoading: isLoadingGetAllSMS,
  } = useCustomFecthQuery(
    key,
    `${process.env.REACT_APP_API}sms/${eventDetails?._id}`,
    token,
    {
      enabled: !!eventDetails?._id,
    }
  );

  const toggleSMS = () => {
    setIsSMSOpen(!isSMSOpen);
  };

  const toggleAIChat = () => {
    setIsChatAiOpen(!isChatAiOpen);
  };

  return (
    <div style={styles.container}>
      {/* Header Section */}
      <div style={styles.headerSection}>
        <h1 style={styles.mainTitle}>SMS</h1>
        <p style={styles.subtitle}>
          Manage and track all your SMS communications. Monitor your SMS credits
          and view sent messages to stay connected with your attendees.
        </p>
      </div>

      {/* New SMS Button */}
      <div style={styles.buttonContainer}>
        <button
          onClick={toggleSMS}
          style={styles.newSmsButton}
          type="button"
        >
          <BsChatLeftText size={isMobile ? 18 : 15} />
          <span>New SMS</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div style={styles.statsContainer}>
        <div style={styles.statCard}>
          <div style={styles.statHeader}>
            <Tooltip text="Total SMS">
              <BsQuestionCircleFill size={16} />
            </Tooltip>
          </div>
          <div style={styles.statLabel}>Total SMS</div>
          <div style={styles.statValue}>{eventDetails?.smsCredits}</div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statHeader}>
            <Tooltip text="Remaining SMS">
              <BsQuestionCircleFill size={16} />
            </Tooltip>
          </div>
          <div style={styles.statLabel}>Remaining SMS</div>
          <div style={styles.statValue}>{eventDetails?.smsCreditsRemaining}</div>
        </div>
      </div>

      {/* Table Section */}
      <div style={styles.tableContainer}>
        <TableSms
          userID={userID}
          token={token}
          eventID={eventDetails?._id}
          mySMS={getAllSMS?.data}
        />
      </div>

      <OffCanvas
        isOffcanvasOpen={isSMSOpen}
        title="New SMS"
        body={
          <FormSMS
            setIsSMSOpen={setIsSMSOpen}
            userId={userID}
            token={token}
            eventID={eventDetails?._id}
            eventDetails={eventDetails}
            isChatAiOpen={isChatAiOpen}
            setIsChatAiOpen={setIsChatAiOpen}
          />
        }
        toggleSaveChanges={toggleSMS}
        directionOffCanvas="end"
        widthOffCanvas={canvasWidth}
      />

      <OffCanvas
        isOffcanvasOpen={isChatAiOpen}
        title="KagoBot"
        body={<AIChat />}
        toggleSaveChanges={toggleAIChat}
        directionOffCanvas="start"
        widthOffCanvas={canvasWidth}
      />

      <style>{`
        @media (max-width: 768px) {
          .sms-container {
            padding: 16px 12px !important;
          }
          
          .stats-wrapper {
            flex-direction: column !important;
            gap: 12px !important;
          }
          
          .stat-card-mobile {
            width: 100% !important;
          }
        }

        @media (max-width: 480px) {
          .main-title {
            font-size: 20px !important;
          }
          
          .subtitle {
            font-size: 13px !important;
          }
        }
      `}</style>
    </div>
  );
};

// Professional Styles
const styles = {
  container: {
    width: "100%",
    maxWidth: "100%",
    padding: "24px 20px",
    boxSizing: "border-box",
    backgroundColor: "#f8f9fa",
    minHeight: "100vh",
  },
  headerSection: {
    marginBottom: "24px",
    paddingBottom: "16px",
    borderBottom: "2px solid #e9ecef",
  },
  mainTitle: {
    fontSize: "26px",
    fontWeight: "700",
    color: "#111827",
    margin: "0 0 8px 0",
    letterSpacing: "-0.5px",
  },
  subtitle: {
    fontSize: "14px",
    color: "#6b7280",
    margin: 0,
    lineHeight: "1.6",
    maxWidth: "800px",
  },
  buttonContainer: {
    marginBottom: "24px",
  },
  newSmsButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "12px 24px",
    background: "linear-gradient(135deg, #f68a20 0%, #e57a10 100%)",
    color: "#ffffff",
    border: "none",
    borderRadius: "10px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 4px 12px rgba(246, 138, 32, 0.3)",
    height: "48px",
  },
  statsContainer: {
    display: "flex",
    gap: "16px",
    marginBottom: "24px",
    flexWrap: "wrap",
  },
  statCard: {
    flex: "1",
    minWidth: "200px",
    padding: "20px",
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    border: "1px solid #e5e7eb",
  },
  statHeader: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: "8px",
  },
  statLabel: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#6b7280",
    marginBottom: "8px",
  },
  statValue: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#111827",
  },
  tableContainer: {
    width: "100%",
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    overflow: "hidden",
  },
};

export default Sms;